import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Navigation, Phone, MessageCircle, Save, X } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";

export default function LocationSettings({ user, onSave, onClose }) {
  const [formData, setFormData] = useState({
    address: user?.address || {
      street: '',
      number: '',
      city: '',
      state: '',
      postal_code: '',
      country: ''
    },
    location: user?.location || {
      latitude: null,
      longitude: null
    },
    phone: user?.phone || '',
    whatsapp: user?.whatsapp || ''
  });
  const [loadingLocation, setLoadingLocation] = useState(false);

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Tu navegador no soporta geolocalización');
      return;
    }

    setLoadingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setFormData({
          ...formData,
          location: {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          }
        });
        setLoadingLocation(false);
        toast.success('Ubicación obtenida correctamente');
      },
      (error) => {
        console.error('Error getting location:', error);
        setLoadingLocation(false);
        toast.error('No se pudo obtener tu ubicación. Verifica los permisos.');
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0
      }
    );
  };

  const handleSave = async () => {
    try {
      await onSave(formData);
      toast.success('Información actualizada correctamente');
      if (onClose) onClose();
    } catch (error) {
      toast.error('Error al guardar');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="w-full max-w-2xl bg-white rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto"
      >
        <div className="sticky top-0 bg-white border-b border-gray-100 p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <MapPin className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Ubicación y Contacto</h2>
              <p className="text-sm text-gray-500">Configura tu dirección y formas de contacto</p>
            </div>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          )}
        </div>

        <div className="p-6 space-y-6">
          {/* GPS Location */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Navigation className="w-5 h-5 text-blue-600" />
                <h3 className="font-semibold text-gray-900">Geolocalización GPS</h3>
              </div>
              <Button
                size="sm"
                onClick={getCurrentLocation}
                disabled={loadingLocation}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {loadingLocation ? 'Obteniendo...' : 'Obtener ubicación'}
              </Button>
            </div>
            
            {formData.location.latitude && formData.location.longitude && (
              <div className="p-3 bg-green-50 rounded-lg">
                <p className="text-sm text-green-800">
                  📍 Lat: {formData.location.latitude.toFixed(6)}, Lng: {formData.location.longitude.toFixed(6)}
                </p>
              </div>
            )}
          </Card>

          {/* Address */}
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="w-5 h-5 text-gray-600" />
              <h3 className="font-semibold text-gray-900">Dirección</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-gray-700 mb-1 block">Calle</label>
                <Input
                  placeholder="Ej: Av. Corrientes"
                  value={formData.address.street}
                  onChange={(e) => setFormData({
                    ...formData,
                    address: { ...formData.address, street: e.target.value }
                  })}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Número</label>
                <Input
                  placeholder="Ej: 1234"
                  value={formData.address.number}
                  onChange={(e) => setFormData({
                    ...formData,
                    address: { ...formData.address, number: e.target.value }
                  })}
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Código Postal</label>
                <Input
                  placeholder="Ej: 1043"
                  value={formData.address.postal_code}
                  onChange={(e) => setFormData({
                    ...formData,
                    address: { ...formData.address, postal_code: e.target.value }
                  })}
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Ciudad</label>
                <Input
                  placeholder="Ej: Buenos Aires"
                  value={formData.address.city}
                  onChange={(e) => setFormData({
                    ...formData,
                    address: { ...formData.address, city: e.target.value }
                  })}
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Provincia/Estado</label>
                <Input
                  placeholder="Ej: CABA"
                  value={formData.address.state}
                  onChange={(e) => setFormData({
                    ...formData,
                    address: { ...formData.address, state: e.target.value }
                  })}
                />
              </div>

              <div className="md:col-span-2">
                <label className="text-sm font-medium text-gray-700 mb-1 block">País</label>
                <Input
                  placeholder="Ej: Argentina"
                  value={formData.address.country}
                  onChange={(e) => setFormData({
                    ...formData,
                    address: { ...formData.address, country: e.target.value }
                  })}
                />
              </div>
            </div>
          </Card>

          {/* Contact */}
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Phone className="w-5 h-5 text-gray-600" />
              <h3 className="font-semibold text-gray-900">Contacto</h3>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Teléfono</label>
                <Input
                  type="tel"
                  placeholder="+54 9 11 1234-5678"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-green-600" />
                  WhatsApp
                </label>
                <Input
                  type="tel"
                  placeholder="+54 9 11 1234-5678"
                  value={formData.whatsapp}
                  onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Incluye el código de país (ej: +54 para Argentina)
                </p>
              </div>
            </div>
          </Card>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-gray-100 p-6 flex gap-3">
          {onClose && (
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancelar
            </Button>
          )}
          <Button onClick={handleSave} className="flex-1 bg-blue-600 hover:bg-blue-700">
            <Save className="w-4 h-4 mr-2" />
            Guardar
          </Button>
        </div>
      </motion.div>
    </div>
  );
}